<template>
  <div
    v-if="uiStore.infoDrawerOpen && !playbackMode"
    class="drawer-backdrop"
    @click.self="closeTargetsDrawer"
  ></div>
  <div class="battle-page" :class="{ playback: playbackMode }">
    <aside
      v-show="!playbackMode"
      class="target-sidebar"
      :class="{ 'is-open': uiStore.infoDrawerOpen }"
      aria-label="Battle targets"
    >
      <header class="drawer-header mobile-drawer-header">
        <h2>Battle Targets</h2>
        <button
          type="button"
          class="drawer-close"
          aria-label="Close"
          title="Close"
          @click="closeTargetsDrawer"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>
      <header class="panel-head">
        <h1>Targets</h1>
        <p class="kicker">Scouted, counter-intel, and unknown peers</p>
      </header>
      <div class="bucket-list">
        <section
          v-for="bucket in buckets"
          :key="bucket.id"
          class="target-bucket"
        >
          <h2>
            {{ bucket.label }}
            <span class="count">{{ bucket.items.length }}</span>
          </h2>
          <p v-if="bucket.items.length === 0" class="empty-copy">
            {{ bucket.empty }}
          </p>
          <ul v-else class="target-rows">
            <li v-for="target in bucket.items" :key="target.id">
              <button
                type="button"
                class="target-row"
                :class="{ active: isSelected(target.id) }"
                @click="onSelectTarget(target.id)"
              >
                <span class="target-title">{{ target.nickname }}</span>
                <span class="target-meta">
                  {{
                    isTargetShielded(target)
                      ? `Shielded · ${target.cityName}`
                      : target.cityName
                  }}
                </span>
              </button>
            </li>
          </ul>
        </section>
      </div>
    </aside>

    <div class="battle-stage-slot">
      <button
        v-if="!playbackMode"
        type="button"
        class="target-pill"
        :class="{ active: uiStore.infoDrawerOpen }"
        @click="openTargetsDrawer"
      >
        <i class="fas fa-bullseye" aria-hidden="true"></i>
        <span>Target: {{ selectedTarget?.nickname || "Select Target" }} ▾</span>
      </button>
      <BattleStage
        :defender-cells="stageVisibleCells"
        :defender-gate-col="stageFormation.gate_col"
        :garrison-hero="stageGarrisonHero"
        :revealed="stageRevealed"
        :fog-all="stageFogAll"
        :attack-cells="gameStore.battleAttackCells"
        :deployed-hero="stageDeployedHero"
        :selected-slug="gameStore.battleSelectedSlug"
        :selected-hero="!!gameStore.battleSelectedHeroId"
        :place-qty="gameStore.battlePlaceQty"
        :has-target="!!selectedTarget"
        :playback-mode="playbackMode"
        :playback-units="playbackUnits"
        :playback-fx="playbackFx"
        @cell-click="onCellClick"
        @cell-right-click="onCellRightClick"
      />
      <BattleGaugeHud
        v-if="isDev && playbackMode"
        :inspect-tick="inspectTick"
        :total-rounds="totalRounds"
        :playing="playbackPlaying"
        :gauges="hudGauges"
        @toggle-playing="togglePlaybackPlaying"
        @inspect="inspectToTick"
      />
    </div>

    <aside
      class="deploy-sidebar"
      :class="{ playback: playbackMode }"
      aria-label="Deploy troops"
      @pointerdown.stop
      @touchstart.stop
    >
      <h2 class="panel-title">{{ playbackMode ? "Battle" : "Deploy" }}</h2>
      <p class="deploy-copy">
        {{
          playbackMode
            ? "Playback in progress. Deployment is locked."
            : "Idle troops only. Garrison and planned quests are reserved."
        }}
      </p>
      <div class="deploy-roster">
        <section v-if="!playbackMode" class="hero-deploy">
          <h3 class="place-label">Hero</h3>
          <p v-if="!gameStore.playerHeroes.length" class="place-hint">
            Activate a hero to deploy them.
          </p>
          <ul v-else class="hero-list">
            <li
              v-for="hero in gameStore.playerHeroes"
              :key="hero.id"
              :class="{
                selectable: canSelectHero(hero),
                active: gameStore.battleSelectedHeroId === hero.id,
                busy: !canSelectHero(hero),
              }"
              @click="onSelectHero(hero)"
            >
              <HeroTagChip
                block
                :name="heroDisplayName(hero)"
                :meta="heroChipMeta(hero)"
                :image="socketedCard(hero)?.image || ''"
                :image-position="socketedCard(hero)?.image_position"
                :rarity="socketedCard(hero)?.rarity || 'common'"
              >
                <span v-if="isHeroBusy(hero)" class="unit-status">On a mission</span>
                <span v-else-if="!socketedCard(hero)" class="unit-status">
                  Socket a hero card
                </span>
              </HeroTagChip>
            </li>
          </ul>
          <p v-if="gameStore.battleSelectedHeroId" class="place-hint hero-place-hint">
            Click an attacker tile to deploy the hero.
          </p>
        </section>
        <ul class="economy-list">
          <li
            v-for="row in deployRows"
            :key="row.slug"
            :class="{
              selectable: canSelect(row) && !playbackMode,
              active: gameStore.battleSelectedSlug === row.slug && !playbackMode,
            }"
            @click="onSelectUnit(row)"
          >
            <i :class="row.icon" :style="{ color: row.color }"></i>
            <div class="unit-copy">
              <span class="economy-name">{{ row.name }}</span>
              <span v-if="row.description" class="unit-desc">{{ row.description }}</span>
            </div>
            <span
              class="economy-amount reserve"
              :title="`${row.remaining} idle ${row.name}`"
            >{{ row.remaining }}</span>
          </li>
        </ul>
      </div>

      <div v-if="selectedRow && !playbackMode" class="place-controls">
        <label class="place-label" for="battle-qty">
          Place {{ selectedRow.name }}
        </label>
        <div class="slider-row">
          <input
            id="battle-qty-range"
            type="range"
            min="0"
            :max="sliderMax"
            :value="gameStore.battlePlaceQty"
            :disabled="sliderMax < 1"
            @input="gameStore.setBattlePlaceQty($event.target.value)"
          />
          <input
            id="battle-qty"
            class="qty-input"
            type="number"
            min="0"
            :max="sliderMax"
            :value="gameStore.battlePlaceQty"
            :disabled="sliderMax < 1"
            @input="gameStore.setBattlePlaceQty($event.target.value)"
          />
        </div>
        <p class="place-hint">
          {{
            sliderMax < 1
              ? `No idle ${selectedRow.name} to place.`
              : `Click an attacker tile to deploy ${gameStore.battlePlaceQty}.`
          }}
        </p>
      </div>

      <div v-if="showUnitStepper" class="qty-stepper">
        <button
          type="button"
          class="qty-step-btn"
          :disabled="gameStore.battlePlaceQty <= 1 || sliderMax < 1"
          aria-label="Decrease quantity"
          @click="nudgeQty(-1)"
        >
          −
        </button>
        <span class="qty-step-value">{{ gameStore.battlePlaceQty }}</span>
        <button
          type="button"
          class="qty-step-btn"
          :disabled="gameStore.battlePlaceQty >= sliderMax || sliderMax < 1"
          aria-label="Increase quantity"
          @click="nudgeQty(1)"
        >
          +
        </button>
      </div>

      <p v-if="selectedTargetShielded" class="truce-copy">
        Protected under Truce{{ selectedShieldCountdown ? ` · ${selectedShieldCountdown}` : "" }}
      </p>
      <p v-if="marchError" class="march-error">{{ marchError }}</p>
      <button
        v-if="!playbackMode"
        type="button"
        class="march-btn"
        :disabled="!canMarch"
        @click="onMarch"
      >
        {{ marchButtonLabel }}
      </button>
      <p v-else class="place-hint playback-hint">
        Watch the field. A report opens when the clash ends.
      </p>
    </aside>

    <BattleUnitDial
      v-if="!playbackMode"
      :open="dialOpen"
      :heroes="dialHeroes"
      :units="dialUnits"
      :selected-slug="gameStore.battleSelectedSlug"
      :selected-hero-id="gameStore.battleSelectedHeroId"
      :place-qty="gameStore.battlePlaceQty"
      :slider-max="sliderMax"
      @update:open="dialOpen = $event"
      @select-hero="onSelectHero"
      @select-unit="onSelectUnit"
      @set-qty="gameStore.setBattlePlaceQty"
    />
  </div>

  <Modal
    :is-visible="reportVisible"
    title="Battle Report"
    confirm-label="Return to Setup"
    secondary-label="Combat Log"
    hide-cancel
    @confirm="closeReport"
    @cancel="closeReport"
    @secondary="openDetailedReport"
  >
    <div v-if="battleResult" class="report-body">
      <p class="report-victor">{{ victorLine }}</p>
      <p class="report-reason">{{ reasonLine }}</p>
      <p v-if="heroXpLine" class="report-xp">{{ heroXpLine }}</p>
      <div class="report-cols">
        <section>
          <h3>Your casualties</h3>
          <ul>
            <li v-for="row in attackerCasualtyRows" :key="row.slug">
              {{ row.name }} −{{ row.lost }}
            </li>
            <li v-if="!attackerCasualtyRows.length">None</li>
          </ul>
        </section>
        <section>
          <h3>Enemy casualties</h3>
          <ul>
            <li v-for="row in defenderCasualtyRows" :key="row.slug">
              {{ row.name }} −{{ row.lost }}
            </li>
            <li v-if="!defenderCasualtyRows.length">None</li>
          </ul>
        </section>
      </div>
      <p v-if="packAwardedLine" class="pack-banner">{{ packAwardedLine }}</p>
      <section v-if="spoilRows.length" class="report-spoils">
        <h3>Spoils</h3>
        <ul>
          <li v-for="row in spoilRows" :key="row.key">
            <i :class="row.icon" :style="{ color: row.color }"></i>
            {{ row.name }} +{{ row.amount }}
          </li>
        </ul>
      </section>
      <p v-else-if="battleResult.winner === 'attacker'" class="place-hint">
        The vaults were empty.
      </p>
      <section v-if="salvageRows.length" class="report-spoils">
        <h3>Battlefield salvage</h3>
        <ul>
          <li v-for="row in salvageRows" :key="row.key">
            <i :class="row.icon" :style="{ color: row.color }"></i>
            {{ row.name }} +{{ row.amount }}
          </li>
        </ul>
      </section>
    </div>
  </Modal>

  <BattleDetailedReportModal
    :is-visible="detailedVisible"
    :battle-result="battleResult"
    :attacker-race="attackerRace"
    :defender-race="defenderRace"
    @close="detailedVisible = false"
  />
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import BattleStage from "../components/BattleStage.vue";
import BattleUnitDial from "../components/BattleUnitDial.vue";
import BattleDetailedReportModal from "../components/BattleDetailedReportModal.vue";
import BattleGaugeHud from "../components/BattleGaugeHud.vue";
import Modal from "../components/Modal.vue";
import HeroTagChip from "../components/HeroTagChip.vue";
import {
  BATTLE_DEPLOY_ORDER,
  applyBattleEvent,
  cellKey,
  gaugesAtTick,
  groupBattleLogByTick,
  playbackEventsFromLog,
  unitsAtTick,
} from "../utils/battleGrid";
import { DEFAULT_GATE_COL } from "../utils/defenseFormation";
import { unitDisplay } from "../utils/unitDisplay";
import { resourceDisplay } from "../utils/resourceDisplay";
import {
  formatShieldCountdown,
  isCityProtected,
} from "../utils/cityShield";

const PLAYBACK_MS = 500;
const isDev = process.env.NODE_ENV !== "production";
const MOBILE_MAX_WIDTH = 768;
const REASON_COPY = {
  gate: "The City Gate was destroyed.",
  attacker_morale: "The attacking army broke and retreated.",
  defender_morale: "The garrison broke. The gate was left defenseless.",
  timeout: "The attackers withdrew when time ran out.",
};

const route = useRoute();
const gameStore = useGameStore();
const uiStore = useUiStore();

const playbackMode = ref(false);
const playbackUnits = ref([]);
const playbackFx = ref([]);
const playbackGateCol = ref(DEFAULT_GATE_COL);
const playbackPlaying = ref(false);
const inspectTick = ref(0);
const totalRounds = ref(0);
const hudGauges = ref({});
const reportVisible = ref(false);
const detailedVisible = ref(false);
const battleResult = ref(null);
const marchError = ref("");
const dialOpen = ref(false);
const nowMs = ref(Date.now());
let playbackTimer = null;
let shieldTimer = null;
let playbackGroups = [];
let playbackIndex = 0;
let playbackInitialUnits = [];
let playbackEvents = [];
let gaugeTicks = [];

const buckets = computed(() => {
  const groups = gameStore.battleTargets;
  return [
    {
      id: "intel",
      label: "Intel Targets",
      empty: "No scout reports yet.",
      items: groups.intel,
    },
    {
      id: "counter",
      label: "Counter-Intel Targets",
      empty: "No captured scouts.",
      items: groups.counter,
    },
    {
      id: "blind",
      label: "Blind Targets",
      empty: "No unknown peers in range.",
      items: groups.blind,
    },
  ];
});

const selectedTarget = computed(() => gameStore.selectedBattleTarget);

const selectedTargetShielded = computed(() =>
  isCityProtected(
    selectedTarget.value?.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  )
);

const selectedShieldCountdown = computed(() =>
  formatShieldCountdown(
    selectedTarget.value?.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  )
);

function isTargetShielded(target) {
  return isCityProtected(
    target?.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  );
}

const stageFormation = computed(() => {
  if (playbackMode.value) {
    return {
      gate_col: playbackGateCol.value,
      cells: [],
      garrisonHero: null,
    };
  }
  const formation = selectedTarget.value?.formation;
  if (!formation) {
    return { gate_col: DEFAULT_GATE_COL, cells: [], garrisonHero: null };
  }
  return {
    gate_col: formation.gate_col ?? DEFAULT_GATE_COL,
    cells: Array.isArray(formation.cells) ? formation.cells : [],
    garrisonHero: formation.garrisonHero || null,
  };
});

const stageRevealed = computed(() => selectedTarget.value?.revealed || []);
const stageFogAll = computed(() =>
  selectedTarget.value ? !!selectedTarget.value.fogAll : true
);

function revealedCellSet(revealed) {
  const set = new Set();
  for (const entry of revealed || []) {
    if (Array.isArray(entry) && entry.length >= 2) {
      set.add(cellKey(entry[0], entry[1]));
    } else if (entry && typeof entry === "object") {
      set.add(cellKey(entry.row, entry.col));
    }
  }
  return set;
}

const stageVisibleCells = computed(() => {
  if (playbackMode.value || stageFogAll.value) return [];
  const visible = revealedCellSet(stageRevealed.value);
  return (stageFormation.value.cells || []).filter((cell) =>
    visible.has(cellKey(cell.row, cell.col))
  );
});

const deployRows = computed(() =>
  BATTLE_DEPLOY_ORDER.map((slug) => {
    const display = unitDisplay(slug, gameStore.userRace);
    return {
      slug,
      name: display.name,
      description: display.description,
      icon: display.icon,
      color: display.color,
      remaining: Math.max(
        0,
        Math.floor(Number(gameStore.battleRemaining?.[slug]) || 0)
      ),
    };
  })
);

const sliderMax = computed(() => Number(gameStore.battleSliderMax) || 0);

const selectedRow = computed(
  () =>
    deployRows.value.find((row) => row.slug === gameStore.battleSelectedSlug) ||
    null
);

const showUnitStepper = computed(
  () =>
    !!gameStore.battleSelectedSlug &&
    !gameStore.battleSelectedHeroId &&
    !playbackMode.value
);

function closeDial() {
  dialOpen.value = false;
}

function closeTargetsDrawer() {
  uiStore.setInfoDrawerOpen(false);
}

function openTargetsDrawer() {
  if (playbackMode.value) return;
  uiStore.setInfoDrawerOpen(true);
}

function onSelectTarget(id) {
  if (playbackMode.value) return;
  gameStore.selectBattleTarget(id);
  closeTargetsDrawer();
}

function nudgeQty(delta) {
  gameStore.setBattlePlaceQty((Number(gameStore.battlePlaceQty) || 0) + delta);
}

function onBattleKeydown(event) {
  if (event.key !== "Escape") return;
  if (dialOpen.value) {
    closeDial();
    return;
  }
  if (uiStore.infoDrawerOpen) {
    closeTargetsDrawer();
  }
}

function onBattleResize() {
  if (window.innerWidth > MOBILE_MAX_WIDTH) {
    closeTargetsDrawer();
    closeDial();
  }
}

const stageGarrisonHero = computed(() => {
  if (playbackMode.value || stageFogAll.value) return null;
  const placed = stageFormation.value.garrisonHero;
  if (!placed) return null;
  const row = Math.floor(Number(placed.row));
  const col = Math.floor(Number(placed.col));
  const heroId = Math.floor(Number(placed.heroId));
  if (!Number.isFinite(heroId) || heroId < 1) return null;
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  if (!revealedCellSet(stageRevealed.value).has(cellKey(row, col))) return null;
  return {
    heroId,
    row,
    col,
    name: "Hero",
    image: "",
  };
});

const stageDeployedHero = computed(() => {
  const placed = gameStore.battleDeployedHero;
  if (!placed) return null;
  const hero = gameStore.playerHeroes.find(
    (entry) => Number(entry.id) === Number(placed.heroId)
  );
  const card = gameStore.socketedHeroCard(placed.heroId);
  return {
    heroId: placed.heroId,
    row: placed.row,
    col: placed.col,
    name: hero?.name || "Hero",
    image: card?.image || "",
  };
});

const canMarch = computed(
  () =>
    !!selectedTarget.value &&
    !selectedTargetShielded.value &&
    gameStore.battleAttackCells.length > 0 &&
    !gameStore.isStartingBattle &&
    !playbackMode.value
);

const marchButtonLabel = computed(() => {
  if (gameStore.isStartingBattle) return "Marching…";
  if (selectedTargetShielded.value) return "Protected";
  return "March";
});

const attackerRace = computed(
  () => battleResult.value?.attackerRace || gameStore.userRace
);
const defenderRace = computed(
  () =>
    battleResult.value?.defenderRace || selectedTarget.value?.race || ""
);

function casualtyName(slug, side) {
  const label = battleResult.value?.summary?.casualties?.[side]?.[slug]?.label;
  if (label) return String(label);
  const race = side === "defender" ? defenderRace.value : attackerRace.value;
  return unitDisplay(slug, race).name;
}

const casualtyRows = (side) => {
  const lost = battleResult.value?.casualties?.[side] || {};
  return BATTLE_DEPLOY_ORDER.map((slug) => {
    const n = Math.max(0, Math.floor(Number(lost[slug]) || 0));
    if (n < 1) return null;
    return { slug, name: casualtyName(slug, side), lost: n };
  }).filter(Boolean);
};

const attackerCasualtyRows = computed(() => casualtyRows("attacker"));
const defenderCasualtyRows = computed(() => casualtyRows("defender"));

const spoilRows = computed(() => {
  const spoils = battleResult.value?.spoils || {};
  return ["gold", "food", "wood", "stone", "iron", "gems"]
    .map((key) => {
      const amount = Math.max(0, Math.floor(Number(spoils[key]) || 0));
      if (amount < 1) return null;
      const display = resourceDisplay(key);
      return { key, amount, ...display };
    })
    .filter(Boolean);
});

const salvageRows = computed(() => {
  const salvage = battleResult.value?.salvage || {};
  return ["food", "wood", "iron", "essence"]
    .map((key) => {
      const amount = Math.max(0, Math.floor(Number(salvage[key]) || 0));
      if (amount < 1) return null;
      return { key, amount, ...resourceDisplay(key) };
    })
    .filter(Boolean);
});

const heroXpLine = computed(() => {
  const gained = battleResult.value?.heroXpGained;
  const xp = Math.max(0, Math.floor(Number(gained?.xp) || 0));
  if (xp < 1) return "";
  const name = gained.name ? String(gained.name) : "Hero";
  return `${name} +${xp} Hero XP`;
});

const packAwardedLine = computed(() => {
  const awarded = battleResult.value?.packAwarded;
  if (!awarded) return "";
  const name =
    typeof awarded === "object" ? awarded.name || "Core Pack" : "Core Pack";
  return `${name} recovered from the field.`;
});

const victorLine = computed(() => {
  if (!battleResult.value) return "";
  return battleResult.value.winner === "attacker"
    ? "Victory"
    : "Defeat";
});

const reasonLine = computed(
  () => REASON_COPY[battleResult.value?.reason] || "The battle has ended."
);

function stopPlaybackTimer() {
  if (playbackTimer != null) {
    clearInterval(playbackTimer);
    playbackTimer = null;
  }
}

function setHudFromTick(tick) {
  inspectTick.value = Math.max(0, Math.floor(Number(tick) || 0));
  hudGauges.value = gaugesAtTick(gaugeTicks, inspectTick.value);
}

function syncPlaybackIndexAfterInspect(tick) {
  const limit = Math.floor(Number(tick) || 0);
  const idx = playbackGroups.findIndex((group) => group.tick > limit);
  playbackIndex = idx < 0 ? playbackGroups.length : idx;
}

function finishPlayback() {
  stopPlaybackTimer();
  playbackPlaying.value = false;
  playbackFx.value = [];
  reportVisible.value = true;
}

function applyPlaybackTick() {
  const group = playbackGroups[playbackIndex];
  if (!group) {
    finishPlayback();
    return;
  }
  playbackUnits.value = playbackUnits.value.filter(
    (unit) => Math.floor(Number(unit.qty) || 0) > 0
  );
  playbackFx.value = group.events
    .filter((event) => event.type === "attack")
    .map((event, index) => ({
      unitId: event.toId,
      damage: event.damage,
      key: `${group.tick}-${index}`,
    }));
  let next = playbackUnits.value;
  for (const event of group.events) {
    next = applyBattleEvent(next, event);
  }
  playbackUnits.value = next;
  playbackIndex += 1;
  setHudFromTick(group.tick);
}

function startPlaybackTimer() {
  stopPlaybackTimer();
  playbackPlaying.value = true;
  playbackTimer = setInterval(applyPlaybackTick, PLAYBACK_MS);
}

function pausePlayback() {
  stopPlaybackTimer();
  playbackPlaying.value = false;
}

function inspectToTick(tick) {
  const max = Math.max(1, totalRounds.value || 1);
  const next = Math.min(max, Math.max(1, Math.floor(Number(tick) || 1)));
  pausePlayback();
  playbackFx.value = [];
  playbackUnits.value = unitsAtTick(playbackInitialUnits, playbackEvents, next);
  setHudFromTick(next);
  syncPlaybackIndexAfterInspect(next);
}

function resumePlayback() {
  if (playbackPlaying.value) return;
  syncPlaybackIndexAfterInspect(inspectTick.value);
  if (playbackIndex >= playbackGroups.length) {
    finishPlayback();
    return;
  }
  applyPlaybackTick();
  if (reportVisible.value) return;
  startPlaybackTimer();
}

function togglePlaybackPlaying() {
  if (playbackPlaying.value) pausePlayback();
  else resumePlayback();
}

function resetPlaybackState() {
  stopPlaybackTimer();
  playbackPlaying.value = false;
  inspectTick.value = 0;
  totalRounds.value = 0;
  hudGauges.value = {};
  playbackGroups = [];
  playbackIndex = 0;
  playbackInitialUnits = [];
  playbackEvents = [];
  gaugeTicks = [];
}

function startPlayback(payload) {
  closeTargetsDrawer();
  closeDial();
  resetPlaybackState();
  playbackMode.value = true;
  playbackInitialUnits = (payload?.initialUnits || []).map((unit) => ({
    ...unit,
  }));
  playbackUnits.value = playbackInitialUnits.map((unit) => ({ ...unit }));
  const gate = playbackUnits.value.find((unit) => unit.slug === "gate");
  playbackGateCol.value =
    gate && Number.isFinite(Number(gate.col))
      ? Math.floor(Number(gate.col))
      : DEFAULT_GATE_COL;
  playbackFx.value = [];
  battleResult.value = payload?.result || null;
  const log = payload?.battleLog;
  playbackEvents = playbackEventsFromLog(log);
  gaugeTicks = Array.isArray(log?.ticks) ? log.ticks : [];
  playbackGroups = groupBattleLogByTick(playbackEvents);
  playbackIndex = 0;
  const lastEventTick = playbackEvents.length
    ? Math.floor(Number(playbackEvents[playbackEvents.length - 1].tick) || 0)
    : 0;
  totalRounds.value =
    gaugeTicks.length ||
    Math.floor(Number(payload?.result?.summary?.rounds) || 0) ||
    lastEventTick;
  if (!playbackGroups.length) {
    if (gaugeTicks.length) {
      inspectToTick(1);
      return;
    }
    finishPlayback();
    return;
  }
  applyPlaybackTick();
  startPlaybackTimer();
}

function closeReport() {
  detailedVisible.value = false;
  reportVisible.value = false;
  playbackMode.value = false;
  playbackUnits.value = [];
  playbackFx.value = [];
  playbackGateCol.value = DEFAULT_GATE_COL;
  battleResult.value = null;
  marchError.value = "";
  resetPlaybackState();
  gameStore.resetBattleSetup();
}

function openDetailedReport() {
  if (!battleResult.value) return;
  detailedVisible.value = true;
}

const isSelected = (id) =>
  Number(gameStore.selectedBattleTargetId) === Number(id);

const canSelect = (row) =>
  !playbackMode.value &&
  (row.remaining > 0 || gameStore.battleSelectedSlug === row.slug);

const onSelectUnit = (row) => {
  if (playbackMode.value) return;
  if (row.remaining < 1 && gameStore.battleSelectedSlug !== row.slug) return;
  gameStore.setBattleSelectedSlug(
    gameStore.battleSelectedSlug === row.slug ? null : row.slug
  );
};

const heroDisplayName = (hero) => {
  if (!hero) return "Hero";
  return hero.name || `Hero #${hero.id}`;
};

const socketedCard = (hero) => gameStore.socketedHeroCard(hero?.id);

const isHeroBusy = (hero) => gameStore.inProgressHeroIds.has(hero?.id);

const canSelectHero = (hero) =>
  !playbackMode.value && !!hero && !isHeroBusy(hero) && !!socketedCard(hero);

const dialHeroes = computed(() =>
  (gameStore.playerHeroes || []).map((hero) => ({
    id: hero.id,
    name: heroDisplayName(hero),
    image: socketedCard(hero)?.image || "",
    selectable: canSelectHero(hero),
  }))
);

const dialUnits = computed(() =>
  deployRows.value.map((row) => ({
    ...row,
    selectable: canSelect(row),
  }))
);

const heroChipMeta = (hero) => `Hero · Level ${hero?.level || 1}`;

const onSelectHero = (hero) => {
  if (!canSelectHero(hero)) return;
  gameStore.setBattleSelectedHeroId(hero.id);
};

const onCellClick = ({ row, col }) => {
  if (playbackMode.value) return;
  gameStore.placeBattleStack(row, col);
};

const onCellRightClick = ({ row, col }) => {
  if (playbackMode.value) return;
  const qty = Math.max(1, Number(gameStore.battlePlaceQty) || 1);
  gameStore.removeBattleUnits(row, col, qty);
};

async function onMarch() {
  if (!canMarch.value) return;
  marchError.value = "";
  try {
    const payload = await gameStore.startBattle();
    if (payload) startPlayback(payload);
  } catch (error) {
    marchError.value = error.message || "Failed to start battle.";
  }
}

function applyEmpireQuery() {
  const raw = route.query.empire;
  if (raw == null || raw === "") return;
  const id = Number(raw);
  if (!Number.isFinite(id)) return;
  const groups = gameStore.battleTargets;
  const match = [...groups.intel, ...groups.counter, ...groups.blind].find(
    (target) => Number(target.id) === id
  );
  if (match) gameStore.selectBattleTarget(match.id);
}

watch(
  () => gameStore.battleSliderMax,
  () => gameStore.clampBattlePlaceQty()
);

watch(
  () => route.query.empire,
  () => applyEmpireQuery()
);

watch(
  () => [playbackMode.value, uiStore.infoDrawerOpen],
  () => {
    if (playbackMode.value && uiStore.infoDrawerOpen) closeTargetsDrawer();
    if (playbackMode.value) closeDial();
  }
);

onMounted(async () => {
  window.addEventListener("keydown", onBattleKeydown);
  window.addEventListener("resize", onBattleResize);
  shieldTimer = setInterval(() => {
    nowMs.value = Date.now();
  }, 1000);
  await gameStore.fetchPlayerAssets();
  await Promise.all([
    gameStore.fetchPlayerResources(),
    gameStore.fetchWorldMap(),
    gameStore.fetchWorldIntel(),
    gameStore.fetchActiveQuests(),
    gameStore.fetchPlayerCards(),
  ]);
  await gameStore.fetchAllHeroSockets();
  applyEmpireQuery();
});

onUnmounted(() => {
  window.removeEventListener("keydown", onBattleKeydown);
  window.removeEventListener("resize", onBattleResize);
  if (shieldTimer) clearInterval(shieldTimer);
  stopPlaybackTimer();
  closeTargetsDrawer();
  closeDial();
});
</script>

<style scoped>
.battle-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr) minmax(260px, 320px);
  overflow: hidden;
}

.battle-page.playback {
  grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
}

.battle-stage-slot {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.target-sidebar,
.deploy-sidebar {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0.9rem 1rem 1rem;
  box-sizing: border-box;
  background: rgba(10, 15, 30, 0.92);
  border: 1px solid rgba(0, 240, 255, 0.28);
  color: #e7ecf1;
  overflow: hidden;
}

.target-sidebar {
  border-left: none;
  border-top: none;
  border-bottom: none;
}

.deploy-sidebar {
  border-right: none;
  border-top: none;
  border-bottom: none;
}

.panel-head h1,
.panel-title {
  margin: 0;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  font-size: 1.15rem;
  text-align: center;
}

.kicker,
.deploy-copy {
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  color: #88c0d0;
  text-align: center;
}

.bucket-list {
  margin-top: 0.8rem;
  overflow: auto;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.target-bucket h2 {
  margin: 0 0 0.35rem;
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c9b48a;
}

.count {
  margin-left: 0.35rem;
  color: #00f0ff;
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.78rem;
  font-style: italic;
}

.target-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.target-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  width: 100%;
  padding: 0.4rem 0.5rem;
  background: rgba(0, 240, 255, 0.08);
  border: 1px solid rgba(0, 240, 255, 0.22);
  border-radius: 4px;
  color: #d8dee9;
  cursor: pointer;
  text-align: left;
}

.target-row:hover,
.target-row.active {
  background: rgba(0, 240, 255, 0.16);
  border-color: #00f0ff;
}

.target-title {
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.target-meta {
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #88c0d0;
}

.economy-list {
  list-style: none;
  margin: 0.9rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: auto;
  min-height: 0;
  flex: 1;
}

.deploy-roster {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.drawer-backdrop,
.mobile-drawer-header,
.target-pill,
.qty-stepper {
  display: none;
}

.hero-deploy {
  margin-top: 0.85rem;
  flex-shrink: 0;
}

.hero-list {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hero-list li {
  border-radius: 8px;
  border: 1px solid transparent;
}

.hero-list li.selectable {
  cursor: pointer;
}

.hero-list li.selectable:hover {
  border-color: rgba(0, 246, 255, 0.35);
}

.hero-list li.active {
  border-color: #f0d060;
  box-shadow: 0 0 10px rgba(240, 208, 96, 0.35);
}

.hero-list li.busy {
  cursor: not-allowed;
  opacity: 0.55;
}

.unit-status {
  font-size: 0.75rem;
  color: #ebcb8b;
}

.economy-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.economy-list li.selectable {
  cursor: pointer;
}

.economy-list li.selectable:hover {
  border-color: rgba(0, 246, 255, 0.35);
}

.economy-list li.active {
  border-color: #00f6ff;
  box-shadow: 0 0 10px rgba(0, 246, 255, 0.25);
  background: rgba(0, 40, 50, 0.55);
}

.economy-list i:first-child {
  width: 22px;
  text-align: center;
  font-size: 1.05rem;
  flex-shrink: 0;
}

.unit-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.economy-name {
  color: #d8d0c0;
  font-size: 0.9rem;
}

.unit-desc {
  color: #8892b0;
  font-size: 0.75rem;
  line-height: 1.35;
}

.economy-amount.reserve {
  font-weight: 700;
  font-size: 0.9rem;
  color: #fff;
  cursor: help;
  min-width: 2ch;
  text-align: right;
  flex-shrink: 0;
}

.place-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 10px;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(0, 246, 255, 0.18);
  border-radius: 8px;
}

.place-label {
  font-family: "Cinzel", serif;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

input[type="range"] {
  flex: 1;
  cursor: pointer;
  accent-color: #00f0ff;
}

.qty-input {
  width: 4.25rem;
  padding: 0.3rem 0.4rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 246, 255, 0.35);
  background: rgba(8, 12, 24, 0.85);
  color: #00f0ff;
  font-family: monospace;
  font-size: 0.95rem;
  text-align: right;
}

.place-hint {
  margin: 0;
  color: #8892b0;
  font-size: 0.75rem;
  line-height: 1.35;
}

.march-error {
  margin: 0.75rem 0 0;
  color: #ff8a8a;
  font-size: 0.78rem;
  text-align: center;
}

.truce-copy {
  margin: 0.7rem 0 0;
  color: #00f0ff;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: center;
}

.march-btn {
  margin-top: 0.85rem;
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid #00f6ff;
  border-radius: 8px;
  background: rgba(0, 246, 255, 0.12);
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
}

.march-btn:hover:not(:disabled) {
  background: rgba(0, 246, 255, 0.22);
}

.march-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.report-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  color: #d8dee9;
}

.report-victor {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 1.4rem;
  color: #f4e6c4;
  text-align: center;
}

.report-reason {
  margin: 0;
  text-align: center;
  color: #88c0d0;
}

.report-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.report-body h3 {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.report-body ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.88rem;
}

.report-spoils ul {
  gap: 0.4rem;
}

.report-spoils li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.report-xp {
  margin: 0 0 0.65rem;
  color: #e6c35c;
  font-size: 0.9rem;
}

.pack-banner {
  margin: 0.85rem 0 0;
  padding: 0.5rem 0.7rem;
  border: 1px solid rgba(214, 158, 46, 0.55);
  background: rgba(214, 158, 46, 0.12);
  color: #e6c35c;
  font-size: 0.86rem;
  letter-spacing: 0.04em;
  text-align: center;
}

@media (max-width: 980px) {
  .battle-page {
    grid-template-columns: minmax(180px, 210px) 1fr minmax(210px, 260px);
  }
  .battle-page.playback {
    grid-template-columns: 1fr minmax(210px, 260px);
  }
}

@media (max-width: 768px) {
  .battle-page,
  .battle-page.playback {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
  }

  .battle-stage-slot {
    width: 100%;
    min-height: 0;
    padding-bottom: 120px;
    overflow: hidden;
    box-sizing: border-box;
    touch-action: none;
  }

  .drawer-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1190;
  }

  .target-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 86vw;
    max-width: 360px;
    height: 100dvh;
    z-index: 1200;
    padding: 0;
    border: none;
    border-right: 1px solid rgba(0, 246, 255, 0.28);
    box-shadow: 8px 0 24px rgba(0, 0, 0, 0.7);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    pointer-events: none;
  }

  .target-sidebar.is-open {
    transform: translateX(0);
    pointer-events: auto;
  }

  .mobile-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-shrink: 0;
    padding: 0.9rem 1rem;
    border-bottom: 1px solid rgba(0, 246, 255, 0.2);
    background: rgba(36, 40, 59, 0.6);
  }

  .mobile-drawer-header h2 {
    margin: 0;
    font-size: 1rem;
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
  }

  .drawer-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid rgba(0, 246, 255, 0.35);
    border-radius: 8px;
    background: rgba(8, 12, 24, 0.78);
    color: #00f6ff;
    cursor: pointer;
  }

  .target-sidebar .panel-head {
    display: none;
  }

  .target-sidebar .bucket-list {
    margin-top: 0;
    padding: 0.9rem 1rem 1rem;
  }

  .target-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 50;
    max-width: calc(100% - 24px);
    height: 36px;
    padding: 0 0.7rem;
    background: rgba(10, 15, 30, 0.92);
    border: 1px solid #00f6ff;
    border-radius: 999px;
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
    font-size: 0.68rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 0 12px rgba(0, 246, 255, 0.35);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .target-pill span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .target-pill.active {
    background: rgba(0, 246, 255, 0.2);
    box-shadow: 0 0 14px rgba(0, 246, 255, 0.5);
  }

  .deploy-sidebar {
    position: fixed;
    bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    left: 0;
    right: 0;
    height: 50px;
    padding: 0;
    gap: 0;
    flex-direction: row;
    align-items: stretch;
    background: rgba(10, 15, 29, 0.95);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: none;
    border-top: 1px solid #00f6ff;
    box-shadow: 0 -8px 24px rgba(0, 246, 255, 0.25);
    z-index: 100;
    overflow: visible;
  }

  .march-error {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 10px;
    right: 10px;
    margin: 0;
    padding: 0.35rem 0.5rem;
    background: rgba(40, 10, 16, 0.95);
    border: 1px solid #ff8a8a;
    border-radius: 6px;
    z-index: 101;
  }

  .deploy-sidebar .panel-title,
  .deploy-sidebar .deploy-copy,
  .deploy-sidebar .deploy-roster,
  .deploy-sidebar .place-controls,
  .deploy-sidebar .place-label,
  .deploy-sidebar .place-hint:not(.playback-hint),
  .deploy-sidebar .unit-desc,
  .deploy-sidebar .qty-stepper {
    display: none;
  }

  .march-btn {
    margin-top: 0;
    flex: 1;
    width: 100%;
    height: 50px;
    padding: 0 16px;
    border: none;
    border-radius: 0;
    font-size: 1rem;
    letter-spacing: 2px;
    background: linear-gradient(
      90deg,
      rgba(0, 80, 90, 0.95),
      rgba(0, 246, 255, 0.28),
      rgba(0, 80, 90, 0.95)
    );
    box-shadow: inset 0 0 18px rgba(0, 246, 255, 0.25);
  }

  .march-btn:hover:not(:disabled) {
    background: linear-gradient(
      90deg,
      rgba(0, 100, 110, 0.98),
      rgba(0, 246, 255, 0.4),
      rgba(0, 100, 110, 0.98)
    );
  }

  .playback-hint {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0;
    font-size: 0.78rem;
    text-align: center;
  }

  .deploy-sidebar.playback .deploy-roster,
  .deploy-sidebar.playback .qty-stepper,
  .deploy-sidebar.playback .march-btn,
  .deploy-sidebar.playback .panel-title,
  .deploy-sidebar.playback .deploy-copy {
    display: none;
  }
}
</style>
